/**
 * Copyright (C) Mairistem
 *
 * Unauthorized copying of this file, via any medium is strictly prohibited.
 *
 * Proprietary and confidential
 */

import _ from 'lodash';
import React from 'react';

import { Form, Grid } from '@mairistem/composants';
import { Section, SectionHeader } from '@mairistem/administres-utils';

import { Steps } from '@/types';
import { AssistantContext } from '@/contexts';
import {
  RANGS_PARENTS_MARIAGE,
  TYPE_COMPONENT_BY_TYPE_ACTE,
  URL_ETATS_MATRIMONIAUX_PARENTS_MARIAGE,
  cleEtatMatrimonialParents,
} from '@/constants';
import { useOptionsReferentiel } from '@/hooks';
import { STYLE_TITRE_SECTION } from '@/components/Common';

import InformationsParent from '../StepParents/InformationsParent';

interface StepParentsMariageProps {
  numeroConjoint: 1 | 2;
}

/**
 * Étape « parents d'un conjoint » d'un acte de mariage : les deux parents, plus
 * l'état matrimonial du COUPLE qu'ils forment.
 *
 * ⚠️ L'état matrimonial est unique pour les deux parents (`am_pmex_ematc`) : il
 * ouvre donc l'étape, avant les deux personnes qu'il qualifie. Le payload le
 * joint malgré tout au bloc du parent 1 — c'est là que l'API le lit.
 *
 * ⚠️ Les rangs dans `data` sont 1-2 pour le premier conjoint et 3-4 pour le
 * second, alors que l'écran affiche « Parent 1 » et « Parent 2 » dans les deux
 * cas : le `ParentForm` de la lib construit ses noms de champs à partir du seul
 * `numeroParent`. La table `RANGS_PARENTS_MARIAGE` porte cette traduction, que
 * les validators et le builder de payload lisent aussi.
 */
const StepParentsMariage = ({ numeroConjoint }: StepParentsMariageProps) => {
  const { data, onChange, errors, selectedTypeActe } =
    React.useContext(AssistantContext);

  const step =
    numeroConjoint === 1 ? Steps.ParentsConjoint1 : Steps.ParentsConjoint2;

  /** Tranche d'erreurs de cette étape, regroupée par parent (`parent{rang}`). */
  const errorsStep = errors?.[step] ?? {};

  const typeComponent = selectedTypeActe
    ? TYPE_COMPONENT_BY_TYPE_ACTE[selectedTypeActe]
    : undefined;

  const rangs = RANGS_PARENTS_MARIAGE[`conjoint${numeroConjoint}`];
  const cleEtat = cleEtatMatrimonialParents(numeroConjoint);

  const { options, items } = useOptionsReferentiel(
    URL_ETATS_MATRIMONIAUX_PARENTS_MARIAGE,
    'codeJustice',
  );

  /**
   * `am_pmex_ematl` est une dénormalisation du libellé : on la dérive de
   * l'option choisie, comme partout ailleurs.
   */
  const handleEtatChange = React.useCallback(
    (event: React.SyntheticEvent | null, { value }: { value: unknown }) => {
      onChange(event, { name: cleEtat, value });

      const item = _.find(items, { codeJustice: value });

      onChange(null, {
        name: `${cleEtat}Libelle`,
        value: item?.libelle ?? null,
      });
    },
    [onChange, items, cleEtat],
  );

  /** Bloc d'un parent, avec son rang dans `data` et son rang à l'écran. */
  const parent = (position: 0 | 1) => {
    const rang = rangs[position];

    return (
      <InformationsParent
        numeroParent={rang}
        libelleParent={`Parent ${position + 1}`}
        titreSection={`INFORMATIONS SUR LE PARENT ${position + 1}`}
        parent={data?.[`parent${rang}`] ?? {}}
        filiationInconnue={data?.[`filiationInconnueParent${rang}`] ?? false}
        errors={errorsStep?.[`parent${rang}`] ?? {}}
        onChange={onChange}
        typeComponent={typeComponent}
        showProfession
      />
    );
  };

  return (
    <div
      className={`assistant-step-parents-conjoint${numeroConjoint}`}
      style={{
        display: 'flex',
        flexDirection: 'column',
        gap: '0.25em',
        width: '100%',
      }}
    >
      <Section title="ÉTAT MATRIMONIAL DES PARENTS" height="auto">
        {/* @ts-ignore */}
        <Form style={{ padding: '8px 14px' }}>
          {/* @ts-ignore */}
          <SectionHeader
            title="État matrimonial"
            required={false}
            style={STYLE_TITRE_SECTION}
          />
          <Grid>
            <Grid.Row style={{ paddingLeft: '1em' }}>
              <Grid.Column computer={8} tablet={16} mobile={16}>
                <Form.Dropdown
                  selection
                  clearable
                  fluid
                  name={cleEtat}
                  label="Etat matrimonial des parents"
                  placeholder="Choisir une situation"
                  options={options}
                  value={data?.[cleEtat] ?? ''}
                  onChange={handleEtatChange}
                />
              </Grid.Column>
            </Grid.Row>
          </Grid>
        </Form>
      </Section>
      {parent(0)}
      {parent(1)}
    </div>
  );
};

export const StepParentsConjoint1 = () => (
  <StepParentsMariage numeroConjoint={1} />
);
export const StepParentsConjoint2 = () => (
  <StepParentsMariage numeroConjoint={2} />
);

export default StepParentsMariage;
